/**
 * EVO — to'lov ma'lumot qatlami (`/api/payments`).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ BU FAYLNING ASOSIY MAVZUSI — KURS FARQI                                  ║
 * ║                                                                          ║
 * ║ Mijozning qarzi USD da, to'lov esa so'mda keladi. To'lov taqsimlanganda  ║
 * ║ so'm USD ga o'sha kundagi kurs bo'yicha aylantiriladi va natija          ║
 * ║ hisob-faktura qoldig'iga TENG CHIQMAYDI.                                 ║
 * ║                                                                          ║
 * ║ Jonli isbot (bugun, 3000-port, `GET /payments/26`):                      ║
 * ║                                                                          ║
 * ║   amount            "23040000.0000" UZS                                  ║
 * ║   fxRate            "12900.00000000"                                     ║
 * ║   amountBase        "1786.0465"      ← USD ekvivalenti                   ║
 * ║   allocations[0].balanceBefore "1800.0000"                               ║
 * ║   allocations[0].balanceAfter  "13.9535"  ← QARZ QOLDI                   ║
 * ║   customerBalanceBase "13.9535"                                          ║
 * ║                                                                          ║
 * ║ Mijoz «to'liq to'ladim» deb o'ylaydi va u O'Z HISOBIDA HAQ: u kelishilgan║
 * ║ so'm summasini to'ladi. $13.95 lik farq kurs 12 800 emas, 12 900 bo'lgani║
 * ║ uchun paydo bo'ldi. Bu raqam kichik ko'rinadi, lekin u HAR TO'LOVDA      ║
 * ║ takrorlanadi va oylab sezilmasa mijoz bilan bahs bo'lib qaytadi.         ║
 * ║                                                                          ║
 * ║ Shuning uchun ekran faqat «qarz qoldi» demaydi — u NIMA UCHUN qolganini  ║
 * ║ ko'rsatadi: summa × kurs = USD, va shu USD qarzdan qancha yopdi.         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ============================================================================
 * `settlementText` SERVERDAN KELADI, LEKIN EKRANDA ISHLATILMAYDI
 * ============================================================================
 * Javobda tayyor jumla bor:
 *   "To'landi: 23040000.0000 UZS = $1786.0465 (kurs: 12900.00000000)\n
 *    Qolgan qarz: $13.9535"
 *
 * U EKRANGA CHIQARILMAYDI va sabab bitta: raqamlar formatlanmagan
 * (`23040000.0000`, `12900.00000000`). `lib/format.ts` qoidasi bo'yicha
 * ko'rinadigan har raqam `formatMoney`/`formatRate` dan chiqishi kerak —
 * «23 040 000 so'm», «12 900». Tayyor jumlani ko'rsatish bu qoidani buzardi
 * va ekranning qolgan qismidan boshqacha ko'rinardi.
 *
 * Muqobil — jumlani brauzerda parsing qilib qayta formatlash — rad etildi:
 * u serverdagi matn shakliga bog'lanib qolardi va matn o'zgargan kuni
 * JIMGINA buzilardi. Endi ekran STRUKTURALI maydonlardan o'zi quradi
 * (`amount`, `currencyCode`, `fxRate`, `amountBase`, `customerBalanceBase`),
 * ya'ni server maydon nomini o'zgartirsa TypeScript emas, jonli tekshiruv
 * ushlaydi — lekin matn shakli o'zgarishi hech narsani buzmaydi.
 *
 * `settlementText` baribir tipda qoldirilgan: u kvitansiya va Telegram
 * xabari uchun manba va uni tipdan olib tashlash «bunday maydon yo'q» degan
 * xato taassurot berardi.
 */
import { api } from './api';
import type { TranslationKey } from './i18n';
import type { Page, SortDir } from './products';

/** `packages/db/src/schema/money.ts` → `PAYMENT_METHODS`. */
export const PAYMENT_METHODS = ['cash', 'bank_transfer', 'card', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** `PaymentListQuerySchema.status` — DTO da AYNAN shu uchtasi. */
export const PAYMENT_STATUSES = ['draft', 'posted', 'cancelled'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** To'lov valyutasi. Qarz esa HAR DOIM USD da (DATA_MODEL §1.4). */
export const PAYMENT_CURRENCIES = ['UZS', 'USD', 'CNY'] as const;
export type PaymentCurrency = (typeof PAYMENT_CURRENCIES)[number];

/**
 * Holat va usul yorliqlari — MA'LUMOT qatlamida, ekranda emas.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN BU YERDA                                                      │
 * │                                                                          │
 * │ To'lov holati IKKI ekranda ko'rinadi: `/payments` va hisob-faktura       │
 * │ kartochkasidagi «Mijozning to'lovlari» bloki. Yorliqlar to'lov ekranida  │
 * │ qolsa, ikkinchi ekran ularni EKRANDAN import qilishi kerak bo'lardi      │
 * │ (butun modul bilan) yoki xom kodni ko'rsatardi — jonli sinovda aynan     │
 * │ ikkinchisi bo'ldi: jadvalda «posted» deb yozilib turdi.                  │
 * │                                                                          │
 * │ `Record` TO'LIQ: server yangi holat qo'shsa va u ro'yxatga kiritilsa,    │
 * │ bu yerda kalit yetishmay KOMPILYATSIYA yiqiladi.                         │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, TranslationKey> = {
  draft: 'payment.status.draft',
  posted: 'payment.status.posted',
  cancelled: 'payment.status.cancelled',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, TranslationKey> = {
  cash: 'payment.method.cash',
  bank_transfer: 'payment.method.bank_transfer',
  card: 'payment.method.card',
  other: 'payment.method.other',
};

export function isKnownPaymentStatus(value: string): value is PaymentStatus {
  return value in PAYMENT_STATUS_LABELS;
}

export function isKnownPaymentMethod(value: string): value is PaymentMethod {
  return value in PAYMENT_METHOD_LABELS;
}

export interface PaymentRow {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly paymentDate: string;
  /** To'lov KELGAN valyutadagi summa — kvitansiyadagi raqam. */
  readonly amount: string;
  readonly currencyCode: string;
  /** O'sha summaning USD ekvivalenti (`amount ÷ fxRate`). */
  readonly amountBase: string;
  readonly allocatedAmount: string;
  readonly method: string;
  readonly status: string;
  /**
   * FAQAT `cashaccount.view` bilan keladi (`payments.service.ts` →
   * `listProjection()`). Sotuvchida u YO'Q: u o'z mijozining to'lov FAKTINI
   * ko'radi, kassa qoldig'ini emas (MATRIX §3.5 izoh¹²).
   */
  readonly cashAccountId?: number;
}

/**
 * Bitta hisob-fakturaga biriktirish natijasi.
 *
 * `balanceBefore` va `balanceAfter` IKKALASI ham qaytariladi va ikkalasi ham
 * kerak: farqi «bu to'lov qancha yopdi» ni beradi, `balanceAfter` esa
 * «qancha qoldi» ni. Faqat bittasini ko'rsatish savolning yarmiga javob
 * berardi.
 */
export interface PaymentAllocation {
  readonly invoiceId: number;
  readonly invoiceNo: string;
  readonly dueDate: string;
  /** To'lov valyutasidagi qism. */
  readonly amount: string;
  /** O'sha qismning USD dagi qiymati — qarzdan aynan shuncha ayriladi. */
  readonly amountBase: string;
  /**
   * Kurs farqidan kelib chiqqan foyda/zarar.
   *
   * Nol bo'lishi mumkin va bu «farq yo'q» degani emas: mijozning
   * `fxDebtPolicy` si `payment_date` bo'lsa, qarz to'lov kunidagi kursda
   * qayta o'lchanadi va farq QOLDIQDA qoladi (`balanceAfter`), alohida
   * foyda/zarar sifatida yozilmaydi. Shuning uchun ekran ikkalasini ham
   * ko'rsatadi.
   */
  readonly fxGainLoss: string;
  readonly balanceBefore: string;
  readonly balanceAfter: string;
  /** Server qo'shgan izoh (masalan qisman yopilgan). Bo'sh bo'lishi mumkin. */
  readonly message: string;
  /** Hisobdan chiqarilgan mayda qoldiq — sozlamadagi chegaradan kichik bo'lsa. */
  readonly writtenOff?: string;
}

export interface Payment extends PaymentRow {
  /** Hujjatga MUHRLANGAN kurs — kunlik kurs keyin o'zgarsa ham o'zgarmaydi. */
  readonly fxRate: string;
  /** Hali hech qaysi hisob-fakturaga biriktirilmagan qism = mijoz avansi. */
  readonly unallocatedAmount: string;
  readonly reference: string | null;
  readonly notes: string | null;
  /**
   * Teskarilash holati — «Teskarilash» tugmasini to'sish uchun.
   *
   * Ilgari bu maydonlar javobda yo'q edi va ekran allaqachon teskarilangan
   * to'lovni ajrata olmasdi: foydalanuvchi tugmani bosib, sabab yozib,
   * tasdiqlagandan KEYIN 409 olardi.
   */
  readonly isReversal: boolean;
  readonly reversalOfId: number | null;
  readonly reversedById: number | null;
  readonly reversalReason: string | null;
  readonly allocations: readonly PaymentAllocation[];
  /** Serverning tayyor jumlasi — fayl boshidagi izohga qarang. */
  readonly settlementText: string;
  /** To'lovdan KEYINGI umumiy qarz, USD. */
  readonly customerBalanceBase: string;
  readonly warnings: readonly string[];
  /** FAQAT `cashaccount.view` bilan. */
  readonly cashAccountName?: string;
}

export interface PaymentListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly status?: PaymentStatus | undefined;
  readonly customerId?: number | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly unallocatedOnly?: boolean | undefined;
}

/** `PAYMENT_SORTABLE` (payments.service.ts) bilan AYNAN bir xil. */
export const PAYMENT_SORTABLE = ['docNo', 'paymentDate', 'amount', 'status', 'createdAt'] as const;

export const paymentKeys = {
  all: ['payments'] as const,
  list: (params: PaymentListParams) => ['payments', 'list', params] as const,
  detail: (id: number) => ['payments', 'detail', id] as const,
  ofCustomer: (customerId: number) => ['payments', 'customer', customerId] as const,
};

export function fetchPaymentList(
  params: PaymentListParams,
  signal?: AbortSignal,
): Promise<Page<PaymentRow>> {
  /*
   * `cashAccountId` filtri ATAYLAB YO'Q, garchi server uni qabul qilsa ham.
   *
   * `assertFiltersAllowed()` uni `cashaccount.view` bilan yopadi va sababi
   * ekranga ham tegishli: to'lovlarni kassalar bo'yicha bir-bir sanab
   * chiqish qaysi hisobga qancha tushganini TIKLAYDI — javobda maydon
   * yo'qligi bunda yordam bermaydi (MATRIX §4.3). Ekranda bu filtr bo'lsa,
   * u faqat bitta rol uchun ishlaydigan va qolganlarda 400 beradigan
   * boshqaruv elementi bo'lardi.
   */
  return api.get<Page<PaymentRow>>('/payments', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      status: params.status,
      customerId: params.customerId,
      from: params.from,
      to: params.to,
      unallocatedOnly: params.unallocatedOnly ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchPayment(id: number, signal?: AbortSignal): Promise<Payment> {
  return api.get<Payment>(`/payments/${id}`, { ...(signal ? { signal } : {}) });
}

/**
 * Mijozning to'lovlari — hisob-faktura kartochkasidagi blok uchun.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN «HISOB-FAKTURANING TO'LOVLARI» EMAS                           │
 * │                                                                          │
 * │ Bunday endpoint YO'Q: `GET /invoices/:id` javobida faqat `lines` bor,    │
 * │ to'lovlar esa teskari tomondan bog'langan (`GET /payments/:id` →         │
 * │ `allocations[].invoiceId`). Ya'ni bitta hisob-fakturaning to'lovlarini    │
 * │ topish uchun mijozning HAR BIR to'lovini alohida ochish kerak bo'lardi   │
 * │ — 30 ta to'lovda 30 ta so'rov.                                           │
 * │                                                                          │
 * │ Shuning uchun kartochka MIJOZNING to'lovlarini ro'yxat qilib ko'rsatadi  │
 * │ va buni OCHIQ aytadi. Qaysi to'lov qaysi hisobni yopgani to'lov          │
 * │ kartochkasida ko'rinadi va u yerga havola bor.                           │
 * │                                                                          │
 * │ Bu API kamchiligi va u hisobotda yozilgan: `GET /invoices/:id` javobiga  │
 * │ `payments` massivi qo'shilsa, bu blok to'g'ridan-to'g'ri o'sha maydonga  │
 * │ o'tadi.                                                                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function fetchCustomerPayments(
  customerId: number,
  signal?: AbortSignal,
): Promise<Page<PaymentRow>> {
  return api.get<Page<PaymentRow>>('/payments', {
    query: { customerId, pageSize: 50, sortBy: 'paymentDate', sortDir: 'desc' },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Yangi to'lov — `payment.create` (admin va moliyachi; rahbarda ham YO'Q).
 *
 * `amount` — SATR. `number` bo'lsa `23040000.4` kabi qiymat float bo'lib
 * yumaloqlanardi va kvitansiyadagi raqam bilan mos kelmasdi.
 */
export interface CreatePaymentBody {
  readonly customerId: number;
  readonly cashAccountId: number;
  readonly paymentDate?: string;
  readonly amount: string;
  readonly currencyCode: PaymentCurrency;
  readonly method?: PaymentMethod;
  readonly reference?: string | null;
  readonly notes?: string | null;
  /**
   * Qo'lda kurs — `fxrate.edit` TALAB QILADI (`payments.service.ts`:
   * `if (!can(ctx, 'fxrate.edit')) throw`). Amaliy sabab: bank real kursi
   * kunlik kursdan farq qiladi va pul AYNAN bank kursida tushadi.
   *
   * Berilmasa server kunlik kursni oladi — ya'ni bo'sh qoldirish TO'G'RI
   * standart holat, «unutilgan maydon» emas.
   */
  readonly fxRate?: string;
}

export function createPayment(body: CreatePaymentBody): Promise<Payment> {
  return api.post<Payment>('/payments', body);
}

/**
 * Post qilish — pul kassaga kiradi va qarz yopiladi.
 *
 * `autoAllocate` default `true`: mijoz odatda «qarzimga» to'laydi, aniq
 * hisob-fakturani ko'rsatmaydi. Taqsimlash EN ESKI MUDDATdan boshlanadi.
 */
export function postPayment(id: number, autoAllocate: boolean): Promise<Payment> {
  return api.post<Payment>(`/payments/${id}/post`, { autoAllocate });
}

/**
 * Qo'lda biriktirish — `payment.post` ruxsati (alohida `allocate` kodi YO'Q).
 *
 * Ekran faqat AVTOMATIK variantni beradi (`allocations` yubormaydi): qo'lda
 * taqsimlash uchun mijozning ochiq hisob-fakturalari ro'yxati kerak, uni
 * beradigan endpoint esa `GET /customers/:id/balance` va u `ar.view` talab
 * qiladi. Ikkita ruxsatga bog'liq forma yozish o'rniga hozircha avtomatik
 * taqsimlash qoldirildi — u eng eski muddatdan boshlaydi va amaliyotda
 * to'lovlarning ko'pchiligi shunday.
 */
export function allocatePayment(id: number): Promise<Payment> {
  return api.post<Payment>(`/payments/${id}/allocate`, {});
}

// ---------------------------------------------------------------------------
// Teskarilash (storno)
// ---------------------------------------------------------------------------

/**
 * Sababning ENG QISQA uzunligi — serverdagi va bazadagi bilan AYNAN bir xil.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN RAQAM BRAUZERDA HAM TURADI                                    ║
 * ║                                                                          ║
 * ║ Zanjir uchta: `ck_payments_reversal_reason` (baza) → `ReversePaymentSchema║
 * ║ .min(5)` (server) → shu doimiy (ekran). Uchinchisi ikkinchisini           ║
 * ║ ALMASHTIRMAYDI — u faqat foydalanuvchini so'rov yuborib, 422 olib,        ║
 * ║ maydonga qaytishdan qutqaradi.                                           ║
 * ║                                                                          ║
 * ║ Shuning uchun ekrandagi xabar ham serverning O'Z jumlasi bilan bir xil    ║
 * ║ («kamida 5 belgi»): ikki xil matn bo'lsa, foydalanuvchi ikkita boshqa     ║
 * ║ qoida bor deb o'ylardi.                                                  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export const PAYMENT_REVERSAL_MIN_REASON = 5;

/** Teskarilash tufayli qoldig'i TIKLANGAN hujjat. */
export interface RestoredAllocation {
  readonly targetTable: string;
  readonly targetId: number;
  readonly docNo: string | null;
  /** Biriktirishda yozilgan baza summa (USD). */
  readonly amountBase: string;
  /** Qoldiq qanchaga tiklandi — HUJJAT valyutasida. */
  readonly restored: string;
  readonly balanceBefore: string;
  readonly balanceAfter: string;
}

/**
 * `POST /payments/:id/reverse` javobi.
 *
 * `message` maydoni ham keladi va u ekranda ISHLATILMAYDI — `settlementText`
 * bilan bir xil sabab (fayl boshidagi izoh): undagi raqamlar formatlanmagan
 * (`23040000.0000 UZS`). Ekran strukturali maydonlardan o'zi quradi.
 */
export interface PaymentReversal {
  /** Bekor qilingan ASL to'lov — endi `cancelled`. */
  readonly original: {
    readonly id: number;
    readonly docNo: string;
    readonly status: string;
    readonly direction: string;
    readonly partyType: string;
    readonly partyId: number | null;
    readonly amount: string;
    readonly currencyCode: string;
    readonly amountBase: string;
    readonly paymentDate: string;
    readonly reversalReason: string;
  };
  /** Yaratilgan teskari (storno) yozuv — asl to'lov O'CHIRILMAYDI. */
  readonly reversal: {
    readonly id: number;
    readonly docNo: string;
    readonly direction: string;
    readonly amount: string;
    readonly currencyCode: string;
    readonly amountBase: string;
    readonly paymentDate: string;
  };
  readonly cashAccountId: number;
  readonly cashBalanceBefore: string;
  readonly cashBalanceAfter: string;
  readonly restored: readonly RestoredAllocation[];
  readonly message: string;
}

/**
 * Teskarilash — to'lovni O'CHIRMAYDI, teskari (storno) yozuv yaratadi.
 *
 * Ruxsat `payment.reverse` va u `payment.post` ning ichida EMAS: post
 * qilish pulni kassaga KIRITISH, teskarilash esa uni hisobotdan CHIQARISH
 * — kassa qoldig'i, mijoz qarzi va daftar birdaniga o'zgaradi
 * (`payments.controller.ts` dagi ramka).
 */
export function reversePayment(id: number, reason: string): Promise<PaymentReversal> {
  return api.post<PaymentReversal>(`/payments/${id}/reverse`, { reason });
}
